import React, { useCallback, useContext, useEffect, useState } from 'react';
import { VehicleSummary } from 'models';
import { Row, Col, Card } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';

import PermissionsGate from 'components/shared/PermissionsGate/PermissionsGate';
import VehicleImageView from 'components/shared/VehicleImageView';
import TagList from 'components/pages/VehicleCollectionBrowser/VehicleCard/components/VehicleCardTagListAndActions/TagListSrp';
import permissions from 'common/permissions';
import { vehicleExtensionCardBuilder } from 'navigation/routes';
import { navigate } from '@reach/router';
import { extensionStrings } from 'common/constants';
import {
  sanitizeWindowPostMessage,
  NewHoverTargetMessage,
  sendMessageToParentViaWindow,
  ID_LENGTH,
} from 'utils/extension';
import { VehicleCardTabType } from 'models';
import VehicleCardReconInventoryTabs from './components/VehicleCardReconInventoryTabs';
import { SessionStateContext } from '../../../../navigation/SessionState';
import VehicleCardProgressBar from './components/VehicleCardProgressBar';
import VehicleDetailsTable from './components/VehicleDetailsTable';
import VehicleCardYearMakeModel from './components/VehicleCardYearMakeModel';
import './VehicleCard.scss';
import { VehicleCardContext } from './VehicleCard';
import { useCurrentLocation, useToken } from 'api';
import { Location } from 'models';
import { useQueryClient } from 'react-query';

// If locationId is different return rooftop.id from user.rooftops
const verifyTenantId = (
  tenantId: string,
  userCurrentTenant: Location | undefined,
  userTenantList: Location[] = []
): string | undefined => {
  const newTenant = userTenantList.find((tenant: Location) => {
    if (tenantId.length === ID_LENGTH) {
      return tenant.id === `ROOFTOP-${tenant}`;
    }

    return tenant.id === tenantId;
  });

  if (
    newTenant &&
    userCurrentTenant &&
    newTenant?.id !== userCurrentTenant?.id
  ) {
    return newTenant.id;
  }

  return undefined;
};

interface VehicleCardExtensionProps {
  vehicle?: VehicleSummary;
  style?: object;
}

const VehicleCardExtension = ({
  vehicle,
  style,
}: VehicleCardExtensionProps) => {
  const [activeCollectionType, setActiveCollectionType] =
    useState<VehicleCardTabType>(
      vehicle?.vehicleCard?.inRecon ? 'recon' : 'inventory'
    );
  const sessionState = useContext(SessionStateContext);
  const { data: tokenData } = useToken();
  const location = useCurrentLocation();
  const queryClient = useQueryClient();

  const onNewHoverTarget = useCallback(
    async (vehicleId: string, tenantId: string) => {
      let locationQueryParam = '';
      const rooftopLocationId = verifyTenantId(
        tenantId,
        location,
        tokenData?.locations
      );
      if (rooftopLocationId) {
        locationQueryParam = `&locationId=${rooftopLocationId}`;
      }
      if (vehicle?.vehicleCard?.id === vehicleId) {
        queryClient.invalidateQueries(`/inventory/${vehicleId}`);
      }
      const vdpCardURL = `${vehicleExtensionCardBuilder(
        vehicleId,
        rooftopLocationId
      )}${locationQueryParam}`;
      navigate(vdpCardURL);
    },
    [location, queryClient, tokenData?.locations, vehicle?.vehicleCard?.id]
  );

  const receiveMessageHandler = useCallback(
    (event: MessageEvent) => {
      if (event.source !== window.parent && !event.data?.action) {
        return;
      }

      switch (event.data.action) {
        case extensionStrings.EXTENSION_EVENT_NEW_HOVER_TARGET: {
          const newEvent = sanitizeWindowPostMessage(event) as
            | NewHoverTargetMessage
            | undefined;
          if (newEvent?.vehicleId && newEvent?.locationId) {
            onNewHoverTarget(newEvent.vehicleId, newEvent.locationId);
          }
          break;
        }
        default:
          break;
      }
    },
    [onNewHoverTarget]
  );

  useEffect(() => {
    window.addEventListener('message', receiveMessageHandler, false);
    return () => {
      window.removeEventListener('message', receiveMessageHandler);
    };
  }, [receiveMessageHandler]);

  useEffect(() => {
    setActiveCollectionType(
      vehicle?.vehicleCard?.inRecon ? 'recon' : 'inventory'
    );
  }, [vehicle?.vehicleCard?.id, vehicle?.vehicleCard?.inRecon]);

  useEffect(() => {
    sessionState.onExtensionCardLoaded();
  }, [sessionState]);

  return (
    <VehicleCardContext.Provider
      value={{
        vehicle: vehicle,
        inExtensionCardMode: sessionState.inExtensionCardMode,
        isOnboarding: false,
        isShowroomMode: false,
        position: 0,
        forceShowEngageTab: false,
        currentTab: activeCollectionType,
        setCurrentTab: setActiveCollectionType,
      }}
    >
      <Card
        className={`VehicleCard2 VehicleCard-mode-${activeCollectionType} VehicleCardExtension`}
      >
        <Row className="VehicleCard-main-row" style={style}>
          <Col>
            <div
              className="d-flex"
              tabIndex={-1}
              role="button"
              onClick={() =>
                sendMessageToParentViaWindow({
                  action: 'extension_card_clicked',
                })
              }
            >
              <VehicleImageView
                thumbnail
                imageUrl={vehicle?.vehicleCard?.vehicleImage}
                skeleton={!vehicle}
              />
              <VehicleCardYearMakeModel size="mobile" />
            </div>
            <Row
              role="button"
              onClick={() =>
                sendMessageToParentViaWindow({
                  action: 'extension_card_clicked',
                })
              }
            >
              <Col>
                <VehicleDetailsTable />
              </Col>
            </Row>
            <Row
              role="button"
              className="mt-1"
              onClick={() =>
                sendMessageToParentViaWindow({
                  action: 'extension_card_clicked',
                })
              }
            >
              <Col className="TagList-Container-extension">
                <PermissionsGate permissions={[permissions.TAGS_VIEW]}>
                  {vehicle ? (
                    <TagList
                      showEditButton
                      truncate
                      vehicleIsSold={vehicle.vehicleCard?.sold ?? true}
                      initialTags={vehicle.tags}
                      vehicleId={vehicle.vehicleCard?.id ?? ''}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </PermissionsGate>
              </Col>
            </Row>
            <Row
              className={`VehicleCard-progress-row ${
                activeCollectionType === 'recon' ? '' : 'mt-0'
              }`}
              onClick={() =>
                sendMessageToParentViaWindow({
                  action: 'extension_card_clicked',
                })
              }
              role="button"
            >
              <Col className="px-0">
                <VehicleCardProgressBar />
              </Col>
            </Row>
            <Row>
              <Col className="VehicleCard-details-container col-recon-inventory">
                <VehicleCardReconInventoryTabs />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </VehicleCardContext.Provider>
  );
};

export default VehicleCardExtension;
